import classNames from "classnames"
import type {
  ChangeEventHandler,
  ComponentPropsWithoutRef,
  Ref,
} from "react"
import {
  forwardRef,
  useCallback,
  useState,
  useMemo,
} from "react"
import styles from "./Dropdown.module.scss"
import { DropdownSelectContext } from "./DropdownSelectContext"
import { UnstyledSelect } from "src/inputs/UnstyledInputs"

/*
 * this select input supports all normal select properties except multiple
 * it should only be used as a single select
 */
export interface Props extends Omit<ComponentPropsWithoutRef<"select">, "multiple"> {
  value?: number | string;
  defaultValue?: number | string;
}

export const DropdownSelect = forwardRef<HTMLSelectElement, Props>(
  function DropdownSelect(props: Props, propRef: Ref<HTMLSelectElement>) {
    const {
      children,
      defaultValue = "",
      onChange: controlledOnChange,
      value: controlledValue,
      ...htmlSelectProps
    } = props

    const [
      uncontrolledValue,
      setUncontrolledValue,
    ] = useState<number | string>(defaultValue)

    const onChange: ChangeEventHandler<HTMLSelectElement> = useCallback(
      (e) => {
        setUncontrolledValue(e.target.value)
        controlledOnChange?.(e)
      },
      [
        setUncontrolledValue,
        controlledOnChange,
      ],
    )

    const value = controlledValue ?? uncontrolledValue

    const dropdownSelectContextValue = useMemo(
      () => ({
        currentValue: value,
      }),
      [value],
    )

    const className = classNames(
      styles.dropdownSelect,
      props.className,
    )

    return (
      <UnstyledSelect
        {...htmlSelectProps}
        className={className}
        onChange={onChange}
        value={value}
        ref={propRef}
      >
        <DropdownSelectContext.Provider value={dropdownSelectContextValue} >
          {children}
        </DropdownSelectContext.Provider>
      </UnstyledSelect>
    )
  },
)
